import { portalText, usePortalTranslation } from "../../../i18n/portal.js";
/**
 * The voice chooser's Play (design §26, the product owner's request of 2026-10-02): each voice says
 * its language's sample line before the customer picks it — in Agent setup's Voices rows and on its Review step
 * ({@link LanguageVoicePlayButton}; plan-015 INT-B deleted the round-D `VoiceChoice` and its `VoiceSamplePlay`).
 *
 * One sample plays at a time. Pressing Play fetches the sample (`voiceSampleAudio`, a WAV from
 * `GET /v1/voices/{voice_id}/sample`), shows "Loading…", then plays it with "Stop" beside it; it ends
 * by itself, or on Stop, or when another Play is pressed. A sample that cannot be fetched or played says
 * "Sample unavailable" beside its button — never a sample in another language or another voice. A
 * fetched sample is kept for the screen's life, so a second Play of it is instant.
 *
 * The prototype draws the button as a ghost button to the right of the voice (`design/WeTalk.dc.html`,
 * the voices list: "Play" / "Playing…").
 *
 * ── THE GREETING (plan-015 GRT, gap G2, owner answer 11, design §28.5.2) ─────────────────────
 * In Agent setup's "Voices" rows the greeting language's Play is "Play <voice> saying the greeting in
 * <language>" (`design/WeTalk.dc.html` 9802–9807): it posts the greeting AS TYPED NOW with that row's
 * voice (`greetingSampleAudio`, `POST /v1/voices/{voice_id}/greeting-sample`) and plays what comes back.
 * Every other language's Play is still that voice's sample line. A greeting is remembered by voice,
 * language and text, so editing it and pressing Play again fetches the new words, and replaying the same
 * words is instant. A greeting that cannot be heard says "Sample unavailable", like a sample; with no
 * greeting written yet, the row plays its sample instead. {@link LanguageVoicePlayButton} is that button.
 */
import { Icon } from "@wetalk/ui";
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";

import { GLYPH } from "../../../shell/glyph.js";
import { useGateway } from "../../../api/provider.js";

/** What plays a fetched sample. The browser's is an `Audio` element over an object URL. */
export interface SamplePlayback {
  play(): Promise<void>;
  pause(): void;
  /** Called once when the sample finished by itself. */
  onEnded(run: () => void): void;
  /** Called once when the browser could not play the sample. */
  onFailed(run: () => void): void;
}

export type SamplePlaybackOf = (audio: Blob) => SamplePlayback;

/** The browser's playback: an `Audio` element over an object URL that is revoked when it stops. */
export function browserSamplePlaybackOf(audio: Blob): SamplePlayback {
  const url = URL.createObjectURL(audio);
  const element = new Audio(url);
  const release = (): void => {
    URL.revokeObjectURL(url);
  };
  element.addEventListener("ended", release, { once: true });
  return {
    play: () => element.play(),
    pause: () => {
      element.pause();
      release();
    },
    onEnded: (run) => {
      element.addEventListener("ended", run, { once: true });
    },
    onFailed: (run) => {
      element.addEventListener(
        "error",
        () => {
          release();
          run();
        },
        { once: true },
      );
    },
  };
}

type Phase = "loading" | "playing";

/** One sample: a voice in a language. */
export function sampleKeyOf(voiceId: string, languageCode: string): string {
  return `${voiceId}:${languageCode}`;
}

/** One greeting: a voice saying these words in a language (plan-015 GRT). Kept in memory only. */
export function greetingKeyOf(voiceId: string, languageCode: string, text: string): string {
  return `greeting:${voiceId}:${languageCode}:${text.trim()}`;
}

export interface VoiceSamplePlayer {
  /** The sample currently loading or playing, and which. */
  readonly current: { readonly key: string; readonly phase: Phase } | null;
  /** The samples that could not be fetched or played on this screen. */
  readonly unavailable: ReadonlySet<string>;
  toggle(voiceId: string, languageCode: string): void;
  /** plan-015 GRT: plays (or stops) the voice saying `text` — the greeting as typed — in the language. */
  toggleGreeting(voiceId: string, languageCode: string, text: string): void;
}

/** The chooser's one player. Stops whatever plays when the screen goes away. */
export function useVoiceSamplePlayer(playbackOf: SamplePlaybackOf): VoiceSamplePlayer {
  const gateway = useGateway();
  const [current, setCurrent] = useState<VoiceSamplePlayer["current"]>(null);
  const [unavailable, setUnavailable] = useState<ReadonlySet<string>>(new Set());
  const fetched = useRef(new Map<string, Blob>());
  const playing = useRef<SamplePlayback | null>(null);
  // Every press takes a new turn; an answer for an older turn is dropped, never played over the new one.
  const turn = useRef(0);

  const stop = useCallback((): void => {
    turn.current += 1;
    playing.current?.pause();
    playing.current = null;
    setCurrent(null);
  }, []);

  useEffect(() => stop, [stop]);

  const play = useCallback(
    (key: string, fetchAudio: () => Promise<Blob>): void => {
      const wasThis = current?.key === key;
      stop();
      if (wasThis) return;
      const mine = turn.current;
      const failed = (): void => {
        if (turn.current !== mine) return;
        playing.current = null;
        setCurrent(null);
        setUnavailable((before) => new Set([...before, key]));
      };
      setUnavailable((before) => {
        if (!before.has(key)) return before;
        const after = new Set(before);
        after.delete(key);
        return after;
      });
      setCurrent({ key, phase: "loading" });
      const known = fetched.current.get(key);
      const audio = known === undefined ? fetchAudio() : Promise.resolve(known);
      audio
        .then(async (blob) => {
          fetched.current.set(key, blob);
          if (turn.current !== mine) return;
          const playback = playbackOf(blob);
          playing.current = playback;
          playback.onEnded(() => {
            if (turn.current !== mine) return;
            playing.current = null;
            setCurrent(null);
          });
          playback.onFailed(failed);
          await playback.play();
          if (turn.current === mine) setCurrent({ key, phase: "playing" });
        })
        .catch(failed);
    },
    [current, playbackOf, stop],
  );

  const toggle = useCallback(
    (voiceId: string, languageCode: string): void => {
      play(sampleKeyOf(voiceId, languageCode), () => gateway.voiceSampleAudio(voiceId, languageCode));
    },
    [gateway, play],
  );

  const toggleGreeting = useCallback(
    (voiceId: string, languageCode: string, text: string): void => {
      play(greetingKeyOf(voiceId, languageCode, text), () => gateway.greetingSampleAudio(voiceId, languageCode, text));
    },
    [gateway, play],
  );

  return { current, unavailable, toggle, toggleGreeting };
}

/** Which key a Play button plays under: the greeting when one is written for its row, else the voice's sample line. */
export function playKeyOf(voiceId: string, languageCode: string, greeting: string | null): string {
  return greeting !== null && greeting.trim() !== "" ? greetingKeyOf(voiceId, languageCode, greeting) : sampleKeyOf(voiceId, languageCode);
}

export interface LanguageVoicePlayButtonProps {
  readonly player: VoiceSamplePlayer;
  readonly voiceId: string;
  readonly voiceName: string;
  readonly languageCode: string;
  readonly languageName: string;
  /** The icon alone (Review, the Voices rows), or the icon and the word. */
  readonly compact: boolean;
  /**
   * The Voices rows' round button (the export of 2026-10-10): an icon in a circle, Play or Pause, its "Sample
   * unavailable" said by the row under it rather than beside a 34-pixel button.
   */
  readonly round?: boolean;
  /**
   * plan-015 GRT: the greeting as typed, when this row is the greeting language's — its Play is then the
   * voice saying the greeting. Null (another language, or no greeting written yet) plays the sample line.
   */
  readonly greeting: string | null;
}

/**
 * The Play button of a voice in a language, in Agent setup's Voices rows and on its Review step (the
 * prototype's `langVoices[].play` and the Review card's voice row). The greeting language's says the
 * greeting; the others say the sample line (`GET /v1/voices/{voice_id}/sample`).
 */
export function LanguageVoicePlayButton({ player, voiceId, voiceName, languageCode, languageName, compact, round = false, greeting }: LanguageVoicePlayButtonProps): ReactNode {
  usePortalTranslation("operations");

  const saysGreeting = greeting !== null && greeting.trim() !== "";
  const key = playKeyOf(voiceId, languageCode, greeting);
  const phase = player.current?.key === key ? player.current.phase : null;
  const label = phase === "loading" ? portalText("operations", "loading_33ce4174") : phase === "playing" ? portalText("operations", "stop_9e253470") : portalText("operations", "play_5d12bd53");
  const aria = portalText("operations", "value_value_value_in_value_c2935860", { v0: String(phase === "loading" ? portalText("operations", "loading_8f26c652") : label), v1: String(voiceName), v2: String(saysGreeting ? portalText("operations", "saying_the_greeting_cd26a50f") : portalText("operations", "sample_8151325d")), v3: String(languageName) });
  return (
    <span className="c-setup-play-wrap">
      <button
        type="button"
        className={
          round
            ? "c-setup-play-round"
            : compact
              ? "btn btn-ghost c-setup-play-icon"
              : phase === null
                ? "btn btn-secondary c-setup-play"
                : "btn btn-primary c-setup-play"
        }
        aria-label={aria}
        title={compact ? aria : undefined}
        aria-pressed={phase !== null}
        disabled={voiceId === ""}
        onClick={() => {
          if (saysGreeting) player.toggleGreeting(voiceId, languageCode, greeting);
          else player.toggle(voiceId, languageCode);
        }}
      >
        <Icon glyph={phase === null ? GLYPH.play : round ? GLYPH.pause : GLYPH.stop} size={compact ? "md" : "sm"} />
        {compact ? null : label}
      </button>
      {!round && player.unavailable.has(key) ? (
        <span className="c-setup-voice-unavailable" role="status">
          {portalText("operations", "sample_unavailable_1ea3d37a")}</span>
      ) : null}
    </span>
  );
}
